import Joi from 'joi'
import clsx from 'clsx'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import Checkbox from '../../../../../../UI/Checkbox'

import SettingHint from '../SettingHint'

import { mapCompanyTypeToName } from '../../../../../../shared/helpers'
import { TEACHER_UPLOAD_COMPANY_TYPES, readTeacherUploadConfig } from '../../../../../../shared/teacherDocumentUpload'

import styles from '../style.module.scss'
import config from '../RequirementsConfig/style.module.scss'

/*
 * „Upload durch die Lehrkraft" (Anforderung #94).
 *
 * Steht in derselben Gruppe wie die Checkliste, weil es genau zu einem Punkt
 * daraus gehört: der Einverständniserklärung der Erziehungsberechtigten, einem
 * persönlichen Nachweis einzelner Schüler:innen. Alle übrigen Punkte der
 * Checkliste erfüllt die Junior Company als Ganzes und sind davon nicht
 * berührt. (Bis 31.10.2026 galt das auch für den Kursnachweis; er ist
 * ausgebaut.)
 *
 * Der Anlass: Laut den Landesbetreuer:innen scheitern gerade Junior Basic
 * Companies regelmäßig daran, dass die Schüler:innen weder die Anmeldung noch
 * den Upload zustande bringen. Deshalb ist die Freigabe im Startwert **nur für
 * JBC** gesetzt.
 *
 * Der Schalter ist bewusst kein weiterer Punkt in der Checkliste: Er nimmt
 * keine Voraussetzung weg und fügt keine hinzu — er ändert nur, **wer** eine
 * bestehende erfüllen darf.
 */

const schema = Joi.object({
	teacherDocumentUpload: Joi.object().keys({
		active: Joi.boolean(),
		companyTypes: Joi.array().items(Joi.string()).default([])
	})
})

function TeacherDocumentUpload(props) {
	const { t } = useTranslation()
	const { register, reset, watch, handleSubmit } = useForm({ resolver: joiResolver(schema) })

	const active = watch('teacherDocumentUpload.active')

	const onSubmit = form => {
		props
			.updateOptions({
				name: 'teacherDocumentUpload',
				value: {
					active: !!form.teacherDocumentUpload?.active,
					companyTypes: form.teacherDocumentUpload?.companyTypes || []
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		reset({ teacherDocumentUpload: readTeacherUploadConfig(props.form) })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="teacherDocumentUpload" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>
					Einverständniserklärung der Erziehungsberechtigten — Upload durch die Lehrkraft
				</h3>
				<SettingHint
					example="Beispiel: eingeschaltet, angehakt nur JBC — dann lädt die Lehrkraft einer Junior Basic Company die Einverständniserklärungen ihrer Schüler:innen selbst hoch und kann eine falsch hochgeladene Datei beanstanden oder löschen; bei JC und JCC bleibt es beim bisherigen Weg."
					warning="Eingeschaltet ohne angehakte Unternehmensform wirkt nirgends. Geprüft und freigegeben wird eine Einverständniserklärung weiterhin ausschließlich von der Administration, und die Schüler:in selbst kann ihre Unterlage jederzeit weiter hochladen oder ersetzen. Das Löschen entfernt die Datei endgültig — das lässt sich nicht rückgängig machen."
				>
					Erlaubt einer Lehrkraft, die Einverständniserklärung der Erziehungsberechtigten an Stelle ihrer
					Schüler:innen hochzuladen. Anlass sind Rückmeldungen der Landesbetreuer:innen: Vor allem in Junior
					Basic Companies scheitern Schüler:innen regelmäßig schon an der Anmeldung und laden ihre
					Einverständniserklärung nie hoch; die Junior Company bleibt dadurch auf Dauer nicht aktivierbar. Wo
					die Freigabe gilt, öffnet die Lehrkraft in der Ansicht „Schüler:innen" die Person und findet im
					Reiter „Nachweise" je Unterlage ein Uploadfeld; hochgeladene Dateien lassen sich dort auch gleich
					ansehen. Dieselbe Freigabe erlaubt zwei weitere Handlungen an einer bereits hochgeladenen Unterlage:
					„Beanstanden" nimmt den Prüfvermerk zurück und lässt die Datei liegen — für den häufigen Fall, dass
					die Erklärung die richtige ist, aber Unterschrift oder eine Seite fehlt. „Löschen" entfernt sie
					endgültig — für den Fall, dass dort etwas liegt, das nicht liegen bleiben darf. Beides verlangt eine
					Begründung; sie geht an die Schüler:in und steht im Verlauf der Junior Company. Das Dokument bleibt
					der Schüler:in zugeordnet — im Verlauf steht zusätzlich, dass die Lehrkraft es hochgeladen,
					beanstandet oder entfernt hat. Ohne Freigabe zeigt die Lehrkraft den Stand der Nachweise weiterhin
					nur an.
				</SettingHint>
				<div className={styles.sectionContent}>
					<label className={clsx(config.switch, styles.field)}>
						<input type="checkbox" {...register('teacherDocumentUpload.active')} />
						<span className={config.track} aria-hidden="true" />
						<span className={config.state}>{active ? t('activated') : t('deactivated')}</span>
					</label>

					<h4 className={styles.miniSectionTitle}>{t('Company types')}</h4>
					<p className={styles.hintLine}>Die Freigabe gilt nur für die hier angehakten Unternehmensformen.</p>
					<div className={styles.choiceRow}>
						{TEACHER_UPLOAD_COMPANY_TYPES.map(type => (
							<Checkbox
								key={type}
								label={`${type} — ${mapCompanyTypeToName(type)}`}
								value={type}
								register={register('teacherDocumentUpload.companyTypes')}
							/>
						))}
					</div>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

TeacherDocumentUpload.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}

export default TeacherDocumentUpload
